// Dashboard header — Dashboard-Filters-01.
//
// Owns the page title, the date-range selector (presets + a custom
// range), and the eight global filter pickers. Reads/writes the
// shared ``DashboardFilterProvider`` so every widget below sees the
// same filter slice. URL is the source of truth (handled by the
// provider).
//
// Filter layout:
//
//   [ Title + subtitle ]                       [ Date range presets ]
//   [ Assignees · Accounts · Categories · Tags · Statuses
//     · Regulatory toggle · Account segment ]

import { Shield, X } from "lucide-react";
import { useMemo, useState } from "react";

import { fetchAccountTags, fetchBrokerLocationsForAccounts, fetchNaicsSectors } from "@/features/accounts/api";
import {
  useAccountsPickerQuery,
  useCategoriesPickerQuery,
  useMembersPickerQuery,
  useTagsPickerQuery,
} from "@/features/commitments/hooks";
import { SearchableSelect } from "@/features/commitments/SearchableSelect";
import { useQuery } from "@tanstack/react-query";

import {
  dateRangeForPreset,
  useDashboardFilters,
  type DateRangePreset,
} from "./filterState";

const PRESET_LABELS: Array<{ preset: DateRangePreset; label: string }> = [
  { preset: "7d", label: "Last 7 days" },
  { preset: "30d", label: "Last 30 days" },
  { preset: "90d", label: "Last 90 days" },
  { preset: "ytd", label: "Year to date" },
];

const STATUS_OPTIONS: Array<{ value: string; label: string; chipClass: string }> = [
  { value: "backlog", label: "Backlog", chipClass: "bg-recessed text-text-secondary" },
  { value: "ready_for_work", label: "Ready", chipClass: "bg-teal-tint text-teal" },
  { value: "in_progress", label: "In progress", chipClass: "bg-teal-tint text-teal" },
  { value: "in_review", label: "In review", chipClass: "bg-gold-tint text-gold-ink" },
  { value: "done", label: "Done", chipClass: "bg-sage-tint text-teal" },
  { value: "cancelled", label: "Cancelled", chipClass: "bg-ember-tint text-ember-ink" },
];


export function DashboardHeader() {
  return (
    <header
      data-testid="dashboard-header"
      className="flex flex-col gap-3 pb-2"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            Dashboard
          </h2>
          <p className="mt-1 max-w-xl text-sm text-text-secondary">
            Operational summary across every Account and Commitment in your
            brokerage. Use the filters below to narrow the view.
          </p>
        </div>
        <DateRangeSelector />
      </div>

      <FilterRow />
    </header>
  );
}


function DateRangeSelector() {
  const filters = useDashboardFilters();
  const [customOpen, setCustomOpen] = useState(false);
  const isCustom = filters.preset === "custom";

  return (
    <div className="flex flex-col items-end gap-1">
      <div
        data-testid="dashboard-date-range"
        className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
      >
        {PRESET_LABELS.map(({ preset, label }) => {
          const active = filters.preset === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => {
                filters.setPreset(preset);
                setCustomOpen(false);
              }}
              data-active={active ? "true" : "false"}
              data-preset={preset}
              aria-pressed={active}
              className={[
                "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
                active
                  ? "bg-teal-tint text-text-primary"
                  : "text-text-secondary hover:text-text-primary",
              ].join(" ")}
            >
              {label}
            </button>
          );
        })}
        <button
          type="button"
          data-testid="dashboard-date-range-custom-toggle"
          data-active={isCustom ? "true" : "false"}
          aria-pressed={isCustom}
          onClick={() => setCustomOpen((v) => !v)}
          className={[
            "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
            isCustom
              ? "bg-teal-tint text-text-primary"
              : "text-text-secondary hover:text-text-primary",
          ].join(" ")}
        >
          Custom
          {isCustom && (
            <span className="ml-1 text-text-tertiary">
              {shortDate(filters.dateRange.startDate)} – {shortDate(filters.dateRange.endDate)}
            </span>
          )}
        </button>
      </div>
      {customOpen && (
        <div
          data-testid="dashboard-date-range-custom-panel"
          className="flex items-center gap-2 rounded-md border border-border-subtle bg-raised px-3 py-2 shadow-sm"
        >
          <label className="flex flex-col gap-0.5 text-[10px] uppercase tracking-wide text-text-tertiary">
            From
            <input
              type="date"
              data-testid="dashboard-date-range-custom-start"
              value={filters.dateRange.startDate}
              onChange={(e) =>
                filters.setDateRange({
                  startDate: e.target.value,
                  endDate: filters.dateRange.endDate,
                })
              }
              className="rounded border border-border-subtle bg-canvas/40 px-2 py-1 text-xs text-text-primary"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-[10px] uppercase tracking-wide text-text-tertiary">
            To
            <input
              type="date"
              data-testid="dashboard-date-range-custom-end"
              value={filters.dateRange.endDate}
              onChange={(e) =>
                filters.setDateRange({
                  startDate: filters.dateRange.startDate,
                  endDate: e.target.value,
                })
              }
              className="rounded border border-border-subtle bg-canvas/40 px-2 py-1 text-xs text-text-primary"
            />
          </label>
        </div>
      )}
    </div>
  );
}


function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}


function FilterRow() {
  const filters = useDashboardFilters();
  const membersQuery = useMembersPickerQuery();
  const accountsQuery = useAccountsPickerQuery();
  const categoriesQuery = useCategoriesPickerQuery();
  const tagsQuery = useTagsPickerQuery();

  const memberOptions = (membersQuery.data?.members ?? []).map((m) => ({
    value: m.id,
    label: m.display_name || m.email,
    hint: m.email,
  }));
  const accountOptions = (accountsQuery.data?.accounts ?? []).map((a) => ({
    value: a.id,
    label: a.legal_name,
    hint: a.dba_name ?? undefined,
  }));
  const categoryOptions = (categoriesQuery.data?.categories ?? []).map((c) => ({
    value: c.slug,
    label: c.display_name,
    hint: c.is_system ? "System" : undefined,
  }));

  const allTags = tagsQuery.data?.tags ?? [];
  const regulatoryTagIds = useMemo(
    () => allTags.filter((t) => t.is_regulatory).map((t) => t.id),
    [allTags],
  );
  const regulatorySet = useMemo(
    () => new Set(regulatoryTagIds),
    [regulatoryTagIds],
  );
  const tagOptions = allTags.map((t) => {
    const impliedOn =
      filters.regulatoryOnly && regulatorySet.has(t.id);
    return {
      value: t.id,
      label: t.display_name,
      hint: t.is_regulatory ? "Regulatory" : undefined,
      // When the regulatory toggle is on, render the chip as
      // implied-selected (greyed-checked). We can't show a check
      // inside the option label string, but we can append a marker.
      trailing: impliedOn ? (
        <span
          aria-hidden="true"
          className="rounded-full bg-recessed px-1.5 py-0.5 text-[10px] text-text-tertiary"
        >
          via Reg.
        </span>
      ) : undefined,
    };
  });

  return (
    <div
      data-testid="dashboard-filter-row"
      className="flex flex-wrap items-center gap-2 text-xs text-text-tertiary"
    >
      {/* Assignees */}
      <FilterPill
        testId="filter-assignees"
        count={(filters.assigneeFilter ?? []).length}
        onClear={() => filters.setAssigneeFilter([])}
      >
        <SearchableSelect
          fieldId="dashboard-assignees"
          multiple
          value={(filters.assigneeFilter ?? [])}
          options={memberOptions}
          triggerDisplay={
            <span className="text-text-primary">
              {(filters.assigneeFilter ?? []).length === 0
                ? "Assignees — all"
                : `Assignees · ${(filters.assigneeFilter ?? []).length}`}
            </span>
          }
          onPick={(id) =>
            filters.setAssigneeFilter([
              ...(filters.assigneeFilter ?? []), id,
            ])
          }
          onUnpick={(id) =>
            filters.setAssigneeFilter(
              (filters.assigneeFilter ?? []).filter((x) => x !== id),
            )
          }
        />
      </FilterPill>

      {/* Accounts */}
      <FilterPill
        testId="filter-accounts"
        count={(filters.clientFilter ?? []).length}
        onClear={() => filters.setClientFilter([])}
      >
        <SearchableSelect
          fieldId="dashboard-accounts"
          multiple
          value={(filters.clientFilter ?? [])}
          options={accountOptions}
          triggerDisplay={
            <span className="text-text-primary">
              {(filters.clientFilter ?? []).length === 0
                ? "Accounts — all"
                : `Accounts · ${(filters.clientFilter ?? []).length}`}
            </span>
          }
          onPick={(id) =>
            filters.setClientFilter([
              ...(filters.clientFilter ?? []), id,
            ])
          }
          onUnpick={(id) =>
            filters.setClientFilter(
              (filters.clientFilter ?? []).filter((x) => x !== id),
            )
          }
        />
      </FilterPill>

      {/* Categories */}
      <FilterPill
        testId="filter-categories"
        count={(filters.category ?? []).length}
        onClear={() => filters.setCategory([])}
      >
        <SearchableSelect
          fieldId="dashboard-categories"
          multiple
          value={(filters.category ?? [])}
          options={categoryOptions}
          triggerDisplay={
            <span className="text-text-primary">
              {(filters.category ?? []).length === 0
                ? "Categories — all"
                : `Categories · ${(filters.category ?? []).length}`}
            </span>
          }
          onPick={(slug) =>
            filters.setCategory([
              ...(filters.category ?? []), slug,
            ])
          }
          onUnpick={(slug) =>
            filters.setCategory(
              (filters.category ?? []).filter((x) => x !== slug),
            )
          }
        />
      </FilterPill>

      {/* Tags */}
      <FilterPill
        testId="filter-tags"
        count={(filters.tag ?? []).length}
        onClear={() => filters.setTag([])}
      >
        <SearchableSelect
          fieldId="dashboard-tags"
          multiple
          value={(filters.tag ?? [])}
          options={tagOptions}
          triggerDisplay={
            <span className="text-text-primary">
              {(filters.tag ?? []).length === 0
                ? "Tags — all"
                : `Tags · ${(filters.tag ?? []).length}`}
            </span>
          }
          onPick={(id) =>
            filters.setTag([
              ...(filters.tag ?? []), id,
            ])
          }
          onUnpick={(id) =>
            filters.setTag(
              (filters.tag ?? []).filter((x) => x !== id),
            )
          }
        />
      </FilterPill>

      {/* Regulatory-only toggle */}
      <button
        type="button"
        data-testid="filter-regulatory-toggle"
        data-active={filters.regulatoryOnly ? "true" : "false"}
        aria-pressed={filters.regulatoryOnly}
        onClick={() => filters.setRegulatoryOnly(!filters.regulatoryOnly)}
        className={[
          "inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs font-medium transition-fast ease-out",
          filters.regulatoryOnly
            ? "bg-gold-tint text-gold-ink"
            : "bg-raised text-text-secondary hover:text-text-primary",
        ].join(" ")}
        title={
          filters.regulatoryOnly
            ? `Showing Commitments with any regulatory tag (${regulatoryTagIds.length}).`
            : "Toggle to filter to regulatory-tagged Commitments."
        }
      >
        <Shield size={12} aria-hidden="true" />
        Regulatory only
        {filters.regulatoryOnly && regulatoryTagIds.length > 0 && (
          <span className="text-[10px] text-gold-ink">
            ({regulatoryTagIds.length})
          </span>
        )}
      </button>

      {/* Status chips (multi) */}
      <StatusChipFilter
        value={filters.statuses ?? []}
        onChange={filters.setStatuses}
      />

      {/* Account segment composite */}
      <AccountSegmentPicker />
    </div>
  );
}


function StatusChipFilter({
  value, onChange,
}: { value: string[]; onChange: (next: string[]) => void }) {
  const selected = new Set(value);
  function toggle(s: string): void {
    if (selected.has(s)) {
      onChange(value.filter((v) => v !== s));
    } else {
      onChange([...value, s]);
    }
  }
  return (
    <div
      data-testid="filter-statuses"
      data-count={value.length}
      role="group"
      aria-label="Status"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      <span className="px-1 text-text-tertiary">Status:</span>
      {STATUS_OPTIONS.map((opt) => {
        const active = selected.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`filter-status-${opt.value}`}
            aria-pressed={active}
            data-active={active ? "true" : "false"}
            onClick={() => toggle(opt.value)}
            className={[
              "rounded px-1.5 py-0.5 text-[11px] font-medium transition-fast ease-out",
              active
                ? opt.chipClass
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
      {value.length > 0 && (
        <button
          type="button"
          data-testid="filter-statuses-clear"
          aria-label="Clear status filter"
          onClick={() => onChange([])}
          className="group rounded p-0.5 text-text-tertiary"
        >
          <X size={12} aria-hidden="true" className="group-hover:stroke-ember" />
        </button>
      )}
    </div>
  );
}


const SECTORS_QUERY_KEY = "dashboard-naics-sectors" as const;
const ACCOUNT_TAGS_QUERY_KEY = "dashboard-account-tags" as const;
const LOCATIONS_QUERY_KEY = "dashboard-broker-locations" as const;


function AccountSegmentPicker() {
  const filters = useDashboardFilters();
  const sectorsQuery = useQuery({
    queryKey: [SECTORS_QUERY_KEY],
    queryFn: ({ signal }) => fetchNaicsSectors({ signal }),
    staleTime: 30 * 60 * 1000,
  });
  const tagsQuery = useQuery({
    queryKey: [ACCOUNT_TAGS_QUERY_KEY],
    queryFn: ({ signal }) => fetchAccountTags({ signal }),
    staleTime: 5 * 60 * 1000,
  });
  const locationsQuery = useQuery({
    queryKey: [LOCATIONS_QUERY_KEY],
    queryFn: ({ signal }) => fetchBrokerLocationsForAccounts({ signal }),
    staleTime: 5 * 60 * 1000,
  });

  const sectors = sectorsQuery.data?.sectors ?? [];
  const accountTags = tagsQuery.data?.rows ?? [];
  const locations = locationsQuery.data?.rows ?? [];

  const activeCount =
    (filters.segment.naicsSector ? 1 : 0)
    + (filters.segment.accountTagId ? 1 : 0)
    + (filters.segment.primaryLocationId ? 1 : 0);

  return (
    <div
      data-testid="filter-segment"
      data-count={activeCount}
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised px-1 py-0.5"
    >
      <span className="px-1 text-text-tertiary">Segment:</span>
      <select
        data-testid="filter-segment-naics"
        value={filters.segment.naicsSector ?? ""}
        onChange={(e) =>
          filters.setSegment({
            ...filters.segment,
            naicsSector: e.target.value || undefined,
          })
        }
        className="cursor-pointer rounded bg-transparent px-1.5 py-0.5 text-xs text-text-primary focus:outline-none"
      >
        <option value="">NAICS — any</option>
        {sectors.map((s) => (
          <option key={s.code} value={s.code}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        data-testid="filter-segment-account-tag"
        value={filters.segment.accountTagId ?? ""}
        onChange={(e) =>
          filters.setSegment({
            ...filters.segment,
            accountTagId: e.target.value || undefined,
          })
        }
        className="cursor-pointer rounded bg-transparent px-1.5 py-0.5 text-xs text-text-primary focus:outline-none"
      >
        <option value="">Tag — any</option>
        {accountTags.map((t) => (
          <option key={t.tag_id} value={t.tag_id}>
            {t.name}
          </option>
        ))}
      </select>
      <select
        data-testid="filter-segment-location"
        value={filters.segment.primaryLocationId ?? ""}
        onChange={(e) =>
          filters.setSegment({
            ...filters.segment,
            primaryLocationId: e.target.value || undefined,
          })
        }
        className="cursor-pointer rounded bg-transparent px-1.5 py-0.5 text-xs text-text-primary focus:outline-none"
      >
        <option value="">Location — any</option>
        {locations.map((l) => (
          <option key={l.location_id} value={l.location_id}>
            {l.name}
            {l.city ? ` · ${l.city}` : ""}
          </option>
        ))}
      </select>
      {activeCount > 0 && (
        <button
          type="button"
          data-testid="filter-segment-clear"
          aria-label="Clear segment filter"
          onClick={() => filters.setSegment({})}
          className="group rounded p-0.5 text-text-tertiary"
        >
          <X size={12} aria-hidden="true" className="group-hover:stroke-ember" />
        </button>
      )}
    </div>
  );
}


function FilterPill({
  testId, count, onClear, children,
}: {
  testId: string;
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}) {
  return (
    <span
      data-testid={testId}
      data-count={count}
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised px-1 py-0.5 text-text-secondary"
    >
      {children}
      {count > 0 && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={onClear}
          aria-label="Clear"
          className="rounded p-0.5 text-text-tertiary hover:text-ember-ink"
        >
          ×
        </button>
      )}
    </span>
  );
}


// Re-export so the dashboard page can prove the date range computation
// is exposed for downstream chunks without opening the filter context.
export { dateRangeForPreset };
